import React from "react";
import { Tooltip } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

// Renders the backend's lcaVersionStatus (IT-3582). The comparison itself is done by
// the backend; this only maps its status to an icon and a tooltip.

export const formatLcaVersion = (version) => {
  if (!version) return "";
  const concat = [
    version.lcaVersion ? `LCA ${version.lcaVersion}` : "",
    version.databaseVersion ? `DB ${version.databaseVersion}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return version.description ? `${concat}  (${version.description})` : concat;
};

export const lastLcaVersionLabel = (lcaVersionStatus) => {
  switch (lcaVersionStatus?.status) {
    case "no_calculation":
      return "No calculation";
    case "unknown":
      return "Unknown (calculated before version tracking)";
    case "version_mismatch":
    case "missing_calculations":
    case "match":
      return formatLcaVersion(lcaVersionStatus.lastLcaVersion);
    default:
      return "N/A";
  }
};

const statusDetail = (lcaVersionStatus) => {
  const { status, articleCount, calculatedArticleCount } = lcaVersionStatus;
  switch (status) {
    case "no_calculation":
      return "No LCA calculation";
    case "unknown":
      return "LCA calculated before version tracking: version unknown";
    case "version_mismatch":
      return "LCA calculated with a different version";
    case "missing_calculations":
      return `${articleCount - calculatedArticleCount} of ${articleCount} references without LCA calculation`;
    case "match":
      return "Traceability and LCA versions match";
    default:
      return null;
  }
};

const TooltipContent = ({ lcaVersionStatus }) => (
  <>
    <div>{statusDetail(lcaVersionStatus)}</div>
    <div>
      Traceability version:{" "}
      {formatLcaVersion(lcaVersionStatus.traceabilityVersion) || "N/A"}
    </div>
    <div>Last LCA version: {lastLcaVersionLabel(lcaVersionStatus)}</div>
  </>
);

/**
 * Mismatch → red alert, unknown → grey help icon, match → green check. Projects
 * without traceability show nothing.
 */
export const LcaVersionStatusIcon = ({ lcaVersionStatus }) => {
  if (!lcaVersionStatus) return null;
  let icon;
  if (lcaVersionStatus.versionMismatch) {
    icon = <ErrorOutlineIcon htmlColor="red" />;
  } else if (lcaVersionStatus.status === "unknown") {
    icon = <HelpOutlineIcon htmlColor="grey" />;
  } else if (lcaVersionStatus.status === "match") {
    icon = <CheckCircleOutlineIcon htmlColor="green" />;
  } else {
    return null;
  }
  return (
    <Tooltip
      title={<TooltipContent lcaVersionStatus={lcaVersionStatus} />}
      enterTouchDelay={0}
    >
      <span data-testid={`lca-version-status-${lcaVersionStatus.status}`}>
        {icon}
      </span>
    </Tooltip>
  );
};
